import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import oauthService from "../services/oauth.service";

/**
 * OAuth Callback Handler
 * Processes the redirect from WordPress after login
 */
const OAuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState({
    loading: true,
    error: null,
  });

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line
  }, []);

  const handleCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");

      // Check for OAuth errors
      if (error) {
        console.error("OAuth error:", error, errorDescription);
        setStatus({
          loading: false,
          error: errorDescription || error,
        });

        setTimeout(() => {
          navigate("/");
        }, 3000);
        return;
      }

      // Validate code
      if (!code) {
        setStatus({
          loading: false,
          error: "No authorization code received from WordPress",
        });

        setTimeout(() => {
          navigate("/");
        }, 3000);
        return;
      }

      console.log("📝 Processing OAuth callback with code...");
      setStatus({ loading: true, error: null });

      // Exchange code for token
      const result = await oauthService.handleCallback(code, state);

      if (result.success) {
        console.log("✅ Login successful! Redirecting to app...");
        navigate("/", { replace: true });
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      console.error("Callback handling failed:", error);
      setStatus({
        loading: false,
        error: error.message || "Failed to complete authentication",
      });

      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  };

  if (status.loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <h2 style={styles.heading}>🔐 Completing Login...</h2>
          <p style={styles.text}>Please wait while we set up your session</p>
        </div>
      </div>
    );
  }

  if (status.error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>❌</div>
          <h2 style={styles.errorHeading}>Login Failed</h2>
          <p style={styles.text}>{status.error}</p>
          <p style={styles.subText}>Redirecting you back in a moment...</p>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    padding: "50px 40px",
    textAlign: "center",
    maxWidth: "500px",
    width: "100%",
  },
  spinner: {
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #4CAF50",
    borderRadius: "50%",
    width: "50px",
    height: "50px",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "12px",
    marginTop: 0,
  },
  text: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "12px",
    lineHeight: "1.6",
  },
  subText: {
    fontSize: "14px",
    color: "#888",
    marginTop: "12px",
  },
  errorIcon: {
    fontSize: "64px",
    marginBottom: "20px",
  },
  errorHeading: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#e74c3c",
    marginBottom: "12px",
    marginTop: 0,
  },
};

export default OAuthCallback;
